// Imports tab: list available sources (with a config form per source)
// and the active/recent import jobs with live progress polling.

import { useEffect, useMemo, useState } from 'react';
import { api, ConfigField, ImportStatus, Source, humanBytes } from './api';

export default function Imports() {
  const [sources, setSources] = useState<Source[] | null>(null);
  const [imports, setImports] = useState<ImportStatus[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);

  // One-shot source load.
  useEffect(() => {
    api.listSources()
      .then((xs) => {
        setSources(xs);
        if (xs.length > 0 && selectedSource === null) {
          setSelectedSource(xs[0].name);
        }
      })
      .catch((e) => setErr(String(e)));
    // intentionally exclude selectedSource from deps — we only want
    // the auto-pick on first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll imports while any are running. Slow back-off (2s) is plenty
  // for an in-process runner.
  useEffect(() => {
    let cancel = false;
    const tick = async () => {
      try {
        const xs = await api.listImports();
        if (!cancel) setImports(xs);
      } catch (e) {
        if (!cancel) setErr(String(e));
      }
    };
    tick();
    const handle = window.setInterval(tick, 2000);
    return () => {
      cancel = true;
      window.clearInterval(handle);
    };
  }, []);

  const active = useMemo(
    () => sources?.find((s) => s.name === selectedSource) ?? null,
    [sources, selectedSource],
  );

  return (
    <div>
      {err && <div className="error-banner">{err}</div>}

      <h2 style={{ fontSize: '0.95rem', marginTop: '1rem' }}>Sources</h2>
      {!sources && <div>loading…</div>}
      {sources && sources.length === 0 && <div>no sources registered.</div>}

      <div style={{ display: 'flex', gap: '1rem' }}>
        <ul style={{ listStyle: 'none', padding: 0, minWidth: '14rem' }}>
          {sources?.map((s) => (
            <li key={s.name}>
              <button
                className={`tab ${selectedSource === s.name ? 'active' : ''}`}
                style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: '0.25rem' }}
                onClick={() => setSelectedSource(s.name)}
              >
                {s.name}
              </button>
            </li>
          ))}
        </ul>

        <div style={{ flex: 1 }}>
          {active && (
            <SourceCard
              source={active}
              onSubmitted={(st) => setImports((curr) => [st, ...curr])}
              onError={setErr}
            />
          )}
        </div>
      </div>

      <h2 style={{ fontSize: '0.95rem', marginTop: '2rem' }}>Imports</h2>
      <div className="import-list">
        {imports.length === 0 && <div className="card">no imports yet.</div>}
        {imports.map((i) => (
          <ImportRow key={i.id} im={i} onCancel={() => api.cancelImport(i.id).catch(setErr)} />
        ))}
      </div>
    </div>
  );
}

function SourceCard({
  source,
  onSubmitted,
  onError,
}: {
  source: Source;
  onSubmitted: (st: ImportStatus) => void;
  onError: (msg: string) => void;
}) {
  // Build initial form state from the schema's defaults. Recompute when
  // the source changes (user clicks a different source in the list).
  const initial = useMemo(() => {
    const out: Record<string, string> = {};
    for (const f of source.config_schema.fields) {
      out[f.name] = f.default != null ? String(f.default) : '';
    }
    return out;
  }, [source]);
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValues(initial);
  }, [initial]);

  const submit = async () => {
    setBusy(true);
    try {
      // Coerce ints from the form's string values; let strings pass
      // through untouched. The backend re-validates.
      const cfg: Record<string, unknown> = {};
      for (const f of source.config_schema.fields) {
        const raw = values[f.name];
        if (raw === '' || raw == null) continue;
        cfg[f.name] = f.type === 'int' ? Number(raw) : raw;
      }
      const st = await api.submitImport(source.name, cfg);
      onSubmitted(st);
    } catch (e) {
      onError(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3>{source.name}</h3>
      <div className="desc">{source.description}</div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {source.config_schema.fields.length === 0 && (
          <div className="desc" style={{ marginBottom: '0.5rem' }}>
            No configuration required.
          </div>
        )}
        {source.config_schema.fields.map((f) => (
          <FieldInput
            key={f.name}
            field={f}
            value={values[f.name] ?? ''}
            onChange={(v) => setValues((curr) => ({ ...curr, [f.name]: v }))}
          />
        ))}
        <button type="submit" disabled={busy} className="tab active" style={{ marginTop: '0.5rem' }}>
          {busy ? 'starting…' : 'Start import'}
        </button>
      </form>
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: ConfigField;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
      <div style={{ fontSize: '0.8rem', marginBottom: '0.15rem' }}>
        {field.label}
        {field.required && <span style={{ color: '#ff7575' }}> *</span>}
      </div>
      {field.type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          style={{ width: '100%', padding: '0.3rem', background: 'transparent', color: 'inherit', border: '1px solid var(--bs-border-color)' }}
        >
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ) : (
        <input
          type={field.type === 'int' ? 'number' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          style={{ width: '100%', padding: '0.3rem', background: 'transparent', color: 'inherit', border: '1px solid var(--bs-border-color)' }}
        />
      )}
      {field.description && (
        <div className="desc" style={{ marginTop: '0.15rem' }}>{field.description}</div>
      )}
    </label>
  );
}

function ImportRow({ im, onCancel }: { im: ImportStatus; onCancel: () => void }) {
  const pct =
    im.bytes_expected > 0
      ? Math.min(100, (im.bytes_written / im.bytes_expected) * 100)
      : null;

  return (
    <div className="import-row">
      <span>{im.source}</span>
      <span className={`phase-${im.phase}`}>{im.phase}</span>
      <span>
        <div style={{ marginBottom: '0.15rem' }}>
          {humanBytes(im.bytes_written)}
          {im.bytes_expected > 0 && ` / ${humanBytes(im.bytes_expected)}`}
          {im.files_expected > 0 && ` · ${im.files_written}/${im.files_expected} files`}
        </div>
        {pct != null && (
          <div className="progress-bar"><div style={{ width: `${pct}%` }} /></div>
        )}
        {im.error && <div className="error-banner">{im.error}</div>}
        {!im.error && im.message && <div style={{ opacity: 0.7, fontSize: '0.75rem' }}>{im.message}</div>}
      </span>
      <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>{im.id}</span>
      <span>
        {im.phase === 'running' && (
          <button className="tab" onClick={onCancel}>cancel</button>
        )}
      </span>
    </div>
  );
}
